import { Box, Typography } from "@mui/material";
import { ReactNode } from "react";

const colors = {
  primary: "#014638",
  primary700: "#165C4E",
  primaryWash: "#DBEFDD",
  yellow: "#F7EE6F",
  cream: "#f7f6f0",
  creamHover: "#F9F8F7",
  body: "#151e16",
  secondary: "#776B5D",
  muted: "#9e9e9e",
  faint: "#bdbdbd",
  border: "#e0e0e0",
  success: "#00BA13",
};

interface Props {
  icon: string;
  title: string;
  priceText: string;
  per?: string;
  description: ReactNode;
  tag?: string;
  limitNote?: string;
  extra?: ReactNode;
  loading?: boolean;
  error?: string;
  quantity: number;
  maxQuantity?: number;
  canIncrement?: boolean;
  onAdd: () => void;
  onRemove?: () => void;
}

const Control = ({
  loading,
  quantity,
  maxQuantity,
  canIncrement = true,
  onAdd,
  onRemove,
}: Pick<
  Props,
  "loading" | "quantity" | "maxQuantity" | "canIncrement" | "onAdd" | "onRemove"
>) => {
  const circleBase = {
    flexShrink: 0,
    width: 28,
    height: 28,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    transition: "all .15s",
  };

  if (loading) {
    return (
      <Box sx={{ ...circleBase, color: colors.primary }}>
        <Box component="i" className="fa-regular fa-spinner-third fa-spin" />
      </Box>
    );
  }

  if (quantity === 0) {
    return (
      <Box
        role="button"
        onClick={(e) => {
          e.stopPropagation();
          onAdd();
        }}
        sx={{
          ...circleBase,
          cursor: "pointer",
          border: `1.5px solid ${colors.border}`,
          backgroundColor: "#fff",
          color: colors.muted,
          "&:hover": { borderColor: colors.primary, color: colors.primary },
        }}
      >
        <Box component="i" className="fa-solid fa-plus" />
      </Box>
    );
  }

  if (maxQuantity && maxQuantity > 1) {
    const stepBtn = {
      width: 28,
      height: 28,
      borderRadius: "50%",
      border: "none",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 12,
      cursor: "pointer",
      backgroundColor: colors.primary,
      color: "#fff",
      "&:hover": { backgroundColor: colors.primary700 },
    };
    return (
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          gap: "2px",
          backgroundColor: "#fff",
          border: `1.5px solid ${colors.primary}`,
          borderRadius: "999px",
          p: "3px",
        }}
      >
        <Box component="button" type="button" onClick={onRemove} sx={stepBtn}>
          <Box
            component="i"
            className={
              quantity === 1 ? "fa-solid fa-trash" : "fa-solid fa-minus"
            }
          />
        </Box>
        <Box
          sx={{
            minWidth: 26,
            textAlign: "center",
            fontWeight: 700,
            fontSize: 15,
            color: colors.body,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {quantity}
        </Box>
        <Box
          component="button"
          type="button"
          onClick={onAdd}
          disabled={!canIncrement}
          sx={{
            ...stepBtn,
            "&:disabled": {
              backgroundColor: colors.border,
              color: colors.faint,
              cursor: "not-allowed",
            },
          }}
        >
          <Box component="i" className="fa-solid fa-plus" />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      role="button"
      onClick={(e) => {
        e.stopPropagation();
        if (onRemove) onRemove();
      }}
      sx={{
        ...circleBase,
        cursor: onRemove ? "pointer" : "default",
        border: `1.5px solid ${colors.primary}`,
        backgroundColor: colors.primary,
        color: "#fff",
      }}
    >
      <Box component="i" className="fa-solid fa-check" />
    </Box>
  );
};

const AddOnCard = ({
  icon,
  title,
  priceText,
  per,
  description,
  tag,
  limitNote,
  extra,
  loading,
  error,
  quantity,
  maxQuantity,
  canIncrement,
  onAdd,
  onRemove,
}: Props) => {
  const selected = quantity > 0;

  return (
    <Box>
      <Box
        role="button"
        onClick={() => {
          if (!selected) onAdd();
        }}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.75,
          width: "100%",
          textAlign: "left",
          cursor: selected ? "default" : "pointer",
          backgroundColor: selected ? colors.primaryWash : colors.cream,
          border: `1.5px solid ${selected ? colors.primary : "transparent"}`,
          borderRadius: "12px",
          p: "16px 18px",
          transition: "border-color .15s, background .15s, box-shadow .15s",
          boxShadow: selected ? "0 4px 16px rgba(1,70,56,0.08)" : "none",
          "&:hover": {
            backgroundColor: selected ? colors.primaryWash : colors.creamHover,
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: "11px",
            backgroundColor: selected ? colors.primary : "#fff",
            color: selected ? colors.yellow : colors.primary,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
          }}
        >
          <Box component="i" className={icon} />
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.625,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Typography
              sx={{ fontWeight: 600, fontSize: 15, color: colors.body }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 15,
                color: colors.success,
                whiteSpace: "nowrap",
              }}
            >
              {priceText}
              {per && (
                <Box
                  component="span"
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.muted,
                    ml: "2px",
                  }}
                >
                  {per}
                </Box>
              )}
            </Typography>
          </Box>

          <Typography
            sx={{ fontSize: 12.5, color: colors.secondary, lineHeight: 1.5 }}
          >
            {description}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              flexWrap: "wrap",
              mt: 0.25,
            }}
          >
            {tag && (
              <Box
                sx={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  backgroundColor: colors.primaryWash,
                  color: colors.primary,
                  px: 1.125,
                  py: 0.375,
                  borderRadius: "999px",
                }}
              >
                {tag}
              </Box>
            )}
            {limitNote && (
              <Box sx={{ fontSize: 11.5, color: colors.muted }}>
                {limitNote}
              </Box>
            )}
            {extra}
          </Box>
        </Box>

        <Box sx={{ alignSelf: "center" }}>
          <Control
            loading={loading}
            quantity={quantity}
            maxQuantity={maxQuantity}
            canIncrement={canIncrement}
            onAdd={onAdd}
            onRemove={onRemove}
          />
        </Box>
      </Box>

      {error && (
        <Typography
          sx={{ fontSize: 11, color: "error.main", mt: 0.75, ml: "58px" }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default AddOnCard;
